import React, { useEffect, useRef, useCallback } from 'lib/react';
import {
  EVGrid, EVPaper,
} from 'lib/components';
import { Link } from 'lib/router';
import Typography from 'commonComponents/typography';
import PaymentInformation from 'commonComponents/paymentInformation';
import { useDispatch, useSelector } from 'lib/redux';
import Banner from 'commonComponents/banner';
import history from 'store/CreateHistory';
import {
  MANAGE_PAYMENT_OPTIONS_ROUTE, SERVICE_AGREEMENT_ROUTE,
} from 'constants/RouteConstants';
import Button from 'lib/components/button';
import { useTranslation } from 'lib/translation';
import { PAYMENT_OPTIONS, PAYMENT_OPTIONS_INDEX } from 'features/managePaymentOptions/ManagePaymentOptions.constants';
import {
  updateManagePaymentsCardDetails, updateManagePaymentsCardConsent,
  updatePurchaseOrderValue,
} from 'features/managePaymentOptions/ManagePaymentOptions.actions';
import { RESET_MANAGE_PAYMENT_ACCOUNT_UPDATE_DETAILS, UPDATE_ACCOUNT_PAYMENT_OPTIONS } from 'constants/ActionTypes';
import TextInputField from 'commonComponents/textInputField';
import { CURRENCY_SYMBOL, ERROR } from 'constants/AppConstants';
import SubmitPaymentOptionStyles from './SubmitPaymentOptionStyles';

function SubmitPaymentOption() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const classes = SubmitPaymentOptionStyles();
  const {
    creditCard, updatedCardDetails, ccConsent, paymentValues, purchaseOrder, cardDetailErrors,
    accountUpdateError, accountUpdateLoading,
  } = useSelector(({ userData, managePaymentOptions }) => ({
    ...managePaymentOptions,
    creditCard: userData.billingInformation?.creditCard,
  }));
  const paymentType = new URLSearchParams(window.location.search).get('type');
  const paymentInfoRef = useRef(null);
  const latestPaymentInfoRef = useRef(null);

  useEffect(() => {
    if (!creditCard || !PAYMENT_OPTIONS[paymentType] || !paymentType) history.push(MANAGE_PAYMENT_OPTIONS_ROUTE);
    return () => dispatch({ type: RESET_MANAGE_PAYMENT_ACCOUNT_UPDATE_DETAILS });
  }, []);

  const paymentInfo = updatedCardDetails?.isUpdated ? { ...updatedCardDetails } : {
    firstName: creditCard?.cardFirstName,
    lastName: creditCard?.cardLastName,
    cardNumber: creditCard?.creditCardNumber,
    cardType: creditCard?.creditCardType,
    expirationDateMonth: creditCard?.cardExpMonth,
    expirationDateYear: creditCard?.cardExpYear,
  };

  // Keep the ref updated with the latest paymentInfo
  useEffect(() => {
    latestPaymentInfoRef.current = paymentInfo;
  }, [paymentInfo]);

  const creditCardConsent = ccConsent ?? creditCard?.ccConsent;

  const handlePaymentInfoChange = useCallback((fieldId, event) => {
    const currentPaymentInfo = latestPaymentInfoRef.current;
    const paymentInformationVal = { ...currentPaymentInfo, isUpdated: true };
    const { value } = event.target;
    const targetId = fieldId || event.target.id;
    paymentInformationVal[targetId] = value;
    dispatch(updateManagePaymentsCardDetails({ ...paymentInformationVal, isUpdated: true }));
  }, []);

  const handleConsentCheckboxChange = () => dispatch(updateManagePaymentsCardConsent(!creditCardConsent));
  const handlePurchaseOrderInputChange = (e) => dispatch(updatePurchaseOrderValue({ purchaseOrder: e.target.value }));

  const handleSave = async () => {
    const transientTokenData = await paymentInfoRef.current.tokenize();
    return dispatch({
      type: UPDATE_ACCOUNT_PAYMENT_OPTIONS, paymentType, paymentInfo, ccConsent: creditCardConsent, transientTokenData,
    });
  };
  return (
    <>
      {accountUpdateError && (
        <Banner
          severity={ERROR}
          message={t('manage_payment__unable_to_submit_order')}
        />
      )}
      <EVGrid className={classes.submitPaymentContainer}>
        <EVGrid className={classes.paymentContainer}>
          <EVPaper className={classes.paymentOptionDetailsContainer}>
            <Typography variant="h3-600" fontColor="ocean-700">{t('manage_payment__payment_option_details')}</Typography>
            <Typography variant="body-400" fontColor="charcoal-700">
              {t('manage_payment__payment_option_details_description')}
            </Typography>
            <EVGrid className={classes.paymentOptionValues}>
              <EVGrid className={classes.optionValue}>
                <Typography variant="body-600" className={classes.optionKeyText}>{t('manage_payment__payment_option')}</Typography>
                <Typography>{PAYMENT_OPTIONS[paymentType]}</Typography>
              </EVGrid>
              <EVGrid className={classes.optionValue}>
                <Typography variant="body-600" className={classes.optionKeyText}>{t('manage_payment__amount')}</Typography>
                <Typography>
                  {CURRENCY_SYMBOL[1]}
                  {parseFloat(paymentValues[paymentType]).toFixed(2)}
                </Typography>
              </EVGrid>
            </EVGrid>
          </EVPaper>
          <EVPaper className={classes.paymentInfoContainer}>
            <Typography variant="h3-600" fontColor="ocean-700">{t('manage_payment__payment_information')}</Typography>
            {paymentType === PAYMENT_OPTIONS_INDEX[1] && (
              <Banner
                message={t('manage_payment__payment_information_alert_message')}
                className={classes.infoAlert}
              />
            )}
            <PaymentInformation
              ref={paymentInfoRef}
              isLoading={false}
              paymentInfo={paymentInfo}
              handleChange={handlePaymentInfoChange}
              ccConsent={creditCardConsent}
              handleConsentCheckboxChange={handleConsentCheckboxChange}
              paymentInfoErrors={cardDetailErrors}
            />
            {paymentType === PAYMENT_OPTIONS_INDEX[0] && (
              <EVGrid>
                <TextInputField
                  inputId="purchaseOrderNumber"
                  inputValue={purchaseOrder}
                  size="small"
                  handleChange={handlePurchaseOrderInputChange}
                  inputLabelProps={{
                    className: classes.purchaseOrderLabel,
                  }}
                  inputFieldProps={{
                    inputProps: { maxLength: 400 },
                    classes: { root: classes.purchaseOrderInput },
                  }}
                  labelText={t('manage_payment__purchase_order')}
                />
              </EVGrid>
            )}
          </EVPaper>
        </EVGrid>
        <EVGrid>
          <EVPaper className={classes.paymentSummaryContainer}>
            <Typography variant="h3-600" fontColor="ocean-700">{t('manage_payment__total_price')}</Typography>
            <EVGrid style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <EVGrid className={classes.paymentSummaryAmountContainer}>
                <Typography>{PAYMENT_OPTIONS[paymentType]}</Typography>
                <Typography>
                  {CURRENCY_SYMBOL[1]}
                  {parseFloat(paymentValues[paymentType]).toFixed(2)}
                </Typography>
              </EVGrid>
              <EVGrid className={classes.paymentSummaryTotalContainer}>
                <Typography variant="body-600">{t('manage_payment__payment_summary_total')}</Typography>
                <Typography variant="body-600">
                  {CURRENCY_SYMBOL[1]}
                  {parseFloat(paymentValues[paymentType]).toFixed(2)}
                </Typography>
              </EVGrid>
              <Typography variant="subtitle-400">
                {t('manage_payment__payment_summary_disclaimer', { type: PAYMENT_OPTIONS[paymentType] })}
                <Link style={{ color: '#007CAD', textDecoration: 'none' }} to={SERVICE_AGREEMENT_ROUTE}>
                  {t('manage_payment__payment_summary_service_agreement')}
                </Link>
                .
              </Typography>
              <EVGrid container justifyContent="space-between" className={classes.buttonContainer}>
                <Button id="editPrepayBtn" variant="outlined" color="primary" className={classes.button} onClick={() => history.goBack()}>
                  {t('common_edit')}
                </Button>
                <Button
                  id="submitPrepayBtn"
                  variant="contained"
                  color="primary"
                  className={classes.button}
                  onClick={handleSave}
                  loading={accountUpdateLoading}
                >
                  {t('common_submit')}
                </Button>
              </EVGrid>
            </EVGrid>
          </EVPaper>
        </EVGrid>
      </EVGrid>
    </>
  );
}

export default SubmitPaymentOption;
